import React from 'react';
import { Bar } from 'react-chartjs-2';
import { WeatherConditions } from './Weather';

type WeatherChartProps = {
	data?: WeatherConditions[];
};

const WeatherChart = ({ data }: WeatherChartProps) => {
	let data24 = data.slice(0, 25);
	const parsedData = {
		labels: data24?.map((el) => new Date(el.dt * 1000).toISOString().split('T')[1].slice(0, 5)),
		datasets: [
			{
				borderColor: 'rgba(255, 41, 0, .1)',
				borderWidth: 2,
				data: data24?.map((el) => el.main?.temp ? Math.round(el.main.temp) : null),
				fill: false,
				label: 'Temperature[°C]',
				type: 'line',
				yAxisID: 'y1',
			},
			{
				backgroundColor: 'rgb(54, 162, 235)',
				borderColor: 'white',
				borderWidth: 2,
				label: 'Snow[mm]',
				data: data24?.map((el) => el.snow ? el.snow['3h'] : 0),
				type: 'bar',
				yAxisID: 'y',
				stack: 'precipitation'
			},
			{
				backgroundColor: 'rgb(135, 206, 250)',
				borderColor: 'white',
				borderWidth: 2,
				label: 'Rain[mm]',
				data: data24?.map((el) => el.rain ? el.rain['3h'] : 0),
				type: 'bar',
				yAxisID: 'y',
				stack: 'precipitation'
			},
		],
	};
	const options = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			tooltip: {
				callbacks: {
					label: (context) => {
						const value = context.raw;
	
						if (context.dataset.label === 'Snow[mm]' || context.dataset.label === 'Rain[mm]') {
							if (value > 0 && value < 1) {
								return '<1 mm';
							}
							return `${Math.round(value)} mm`;
						}	
						return `${value} °C`;
					},
				},
			},
		},
		scales: {
			x: {
				grid: {
					drawTicks: false,
					color: (context) => {
						const index = context.index;
						if (index === undefined || !data24[index]) return 'rgba(0, 0, 0, 0.1)';
						
						const currentTime = new Date(data24[index].dt * 1000);
						const prevTime = new Date(data24[index - 1]?.dt * 1000);
	
						if (index > 0 && currentTime.getDate() !== prevTime.getDate()) {
							return 'rgba(0, 0, 0, 0.2)';
						}
						return 'rgba(0, 0, 0, 0.1)';
					},
					lineWidth: (context) => {
						const index = context.index;
						if (index === undefined || !data24[index]) return 1;
	
						const currentTime = new Date(data24[index].dt * 1000);
						const prevTime = new Date(data24[index - 1]?.dt * 1000);
	
						if (index > 0 && currentTime.getDate() !== prevTime.getDate()) {
							return 2;
						}
						return 1;
					},
				},
				ticks: {
					labelOffset: -25,
					autoSkip: false,
					align: 'center',
					maxRotation: 45,
					minRotation: 0,
				},
				position: 'bottom',
			},
			secondXAxis: {
				type: 'category',
				position: 'bottom',
				labels: data24.map((el) => {
					const currentTime = new Date(el.dt * 1000);
					if (currentTime.getHours() === 13) {
						return currentTime.toLocaleDateString('en-GB');
					}
					return '';
				}),
				grid: {
					drawOnChartArea: false,
				},
				ticks: {
					autoSkip: false,
					display: true,
					align: 'center',
					maxRotation: 0,
				},
			},			
			y: {
				stacked: true,
				type: 'linear',
				display: true,
				position: 'left',
				ticks: {
					stepSize: 10,
				},
				title: {
					display: true,
					text: '[mm]',
					font: {
						size: 11,
					},
				},
			},
			y1: {
				stacked: true,
				type: 'linear',
				display: true,
				position: 'right',
				grid: {
					drawOnChartArea: false,
				},
				ticks: {
					stepSize: 10,
				},
				title: {
					display: true,
					text: '[°C]',
					font: {
						size: 11,
					},
				},
			},
		},
	};

	return <Bar data={parsedData} options={options} width={100} height={180} />;
};

export default React.memo(WeatherChart);
